import { z } from "zod";

import {
  INTERNAL_API_KEY_DEFAULT_RATE_LIMIT,
  INTERNAL_API_KEY_MAX_IPS,
  INTERNAL_API_KEY_MAX_RATE_LIMIT,
} from "../constants/internalApiKeys.constants";
import type {
  InternalApiKey,
  InternalApiKeyAllowedIp,
  InternalApiKeyPayload,
  InternalApiKeyPermission,
  InternalApiKeyStatus,
} from "../types/internalApiKeys.types";

const DEFAULT_TTL_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;
const IPV4_OCTET = "(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)";
const IPV4 = new RegExp(`^${IPV4_OCTET}(\\.${IPV4_OCTET}){3}$`);
const MAPPED_IPV4 = /^::ffff:(?=\d+\.\d+\.\d+\.\d+$)/;

/** `expires_on` is `YYYY-MM-DD`, the last day the key works. */
export type InternalApiKeyFormValues = z.infer<ReturnType<typeof buildInternalApiKeySchema>>;

export interface InternalApiKeyFormMessages {
  nameRequired: string;
  permissionsRequired: string;
  organizationsRequired: string;
  expiryRequired: string;
  rateLimitInvalid: string;
  tooManyIps: string;
}

export function buildInternalApiKeySchema(messages: InternalApiKeyFormMessages) {
  return z
    .object({
      name: z.string().trim().min(3, messages.nameRequired).max(80),
      description: z.string().trim().max(500),
      all_permissions: z.boolean(),
      // The catalog comes from the backend, which also rejects anything outside it.
      permissions: z.array(z.custom<InternalApiKeyPermission>((value) => typeof value === "string")),
      all_organizations: z.boolean(),
      organization_ids: z.array(z.string()),
      allowed_ips: z
        .array(
          z.object({
            ip: z.string(),
            label: z.string().optional(),
          }) satisfies z.ZodType<InternalApiKeyAllowedIp>,
        )
        .max(INTERNAL_API_KEY_MAX_IPS, messages.tooManyIps),
      allow_low_risk_without_ip: z.boolean(),
      expires_on: z.string().min(1, messages.expiryRequired),
      rate_limit_per_minute: z
        .string()
        .regex(/^\d+$/, messages.rateLimitInvalid)
        .refine((value) => {
          const n = Number(value);
          return n >= 1 && n <= INTERNAL_API_KEY_MAX_RATE_LIMIT;
        }, messages.rateLimitInvalid),
    })
    .superRefine((values, ctx) => {
      if (!values.all_permissions && values.permissions.length === 0) {
        ctx.addIssue({ code: "custom", path: ["permissions"], message: messages.permissionsRequired });
      }
      if (!values.all_organizations && values.organization_ids.length === 0) {
        ctx.addIssue({ code: "custom", path: ["organization_ids"], message: messages.organizationsRequired });
      }
    });
}

export function toDateInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * The last day a new expiry may fall on. One day short of the backend cap, so the end of that day never
 * overshoots it.
 */
export function maxExpiryDate(maxTtlDays: number, now = new Date()): Date {
  return new Date(now.getTime() + (maxTtlDays - 1) * DAY_MS);
}

export function defaultFormValues(
  key: InternalApiKey | null,
  maxTtlDays: number,
  now = new Date(),
): InternalApiKeyFormValues {
  if (key) {
    return {
      name: key.name,
      description: key.description ?? "",
      all_permissions: key.all_permissions,
      permissions: key.permissions,
      all_organizations: key.all_organizations,
      organization_ids: key.organization_ids,
      allowed_ips: key.allowed_ips,
      allow_low_risk_without_ip: key.allow_low_risk_without_ip ?? false,
      expires_on: toDateInput(new Date(key.expires_at)),
      rate_limit_per_minute: String(key.rate_limit_per_minute),
    };
  }
  const days = Math.min(DEFAULT_TTL_DAYS, maxTtlDays - 1);
  return {
    name: "",
    description: "",
    all_permissions: false,
    permissions: [],
    all_organizations: false,
    organization_ids: [],
    allowed_ips: [],
    allow_low_risk_without_ip: false,
    expires_on: toDateInput(new Date(now.getTime() + days * DAY_MS)),
    rate_limit_per_minute: String(INTERNAL_API_KEY_DEFAULT_RATE_LIMIT),
  };
}

/** The key keeps working through the whole chosen day, in the browser's time zone. */
export function expiryToIso(expiresOn: string): string {
  const [y, m, d] = expiresOn.split("-").map(Number);
  return new Date(y, m - 1, d, 23, 59, 59).toISOString();
}

export function toPayload(values: InternalApiKeyFormValues): InternalApiKeyPayload {
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    all_permissions: values.all_permissions,
    permissions: values.all_permissions ? [] : values.permissions,
    all_organizations: values.all_organizations,
    organization_ids: values.all_organizations ? [] : values.organization_ids,
    allowed_ips: values.allowed_ips,
    // Sent only when on: a backend without the field refuses unknown ones, and off is its default anyway.
    ...(values.allow_low_risk_without_ip ? { allow_low_risk_without_ip: true } : {}),
    expires_at: expiryToIso(values.expires_on),
    rate_limit_per_minute: Number(values.rate_limit_per_minute),
  };
}

const sameItems = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((item) => b.includes(item));

export const sameIps = (a: readonly InternalApiKeyAllowedIp[], b: readonly InternalApiKeyAllowedIp[]) =>
  a.length === b.length &&
  a.every((entry, index) => entry.ip === b[index].ip && (entry.label ?? "") === (b[index].label ?? ""));

/**
 * What an edit sends: only what changed, so a form opened before someone else's edit never writes their lists
 * back. The form only knows days, so an untouched date leaves `expires_at` out: re-sending the end of that
 * day would quietly move a key that expires at 10:00 to 23:59:59.
 */
export function toUpdatePayload(
  values: InternalApiKeyFormValues,
  original: InternalApiKey,
): Partial<InternalApiKeyPayload> {
  const next = toPayload(values);
  const patch: Partial<InternalApiKeyPayload> = {};
  if (next.name !== original.name) patch.name = next.name;
  if (next.description !== (original.description ?? null)) patch.description = next.description;
  if (
    next.all_permissions !== original.all_permissions ||
    !sameItems(next.permissions, original.all_permissions ? [] : original.permissions)
  ) {
    patch.all_permissions = next.all_permissions;
    patch.permissions = next.permissions;
  }
  if (
    next.all_organizations !== original.all_organizations ||
    !sameItems(next.organization_ids, original.all_organizations ? [] : original.organization_ids)
  ) {
    patch.all_organizations = next.all_organizations;
    patch.organization_ids = next.organization_ids;
  }
  if (!sameIps(next.allowed_ips, original.allowed_ips)) patch.allowed_ips = next.allowed_ips;
  if (values.allow_low_risk_without_ip !== (original.allow_low_risk_without_ip ?? false)) {
    patch.allow_low_risk_without_ip = values.allow_low_risk_without_ip;
  }
  if (values.expires_on !== toDateInput(new Date(original.expires_at))) patch.expires_at = next.expires_at;
  if (next.rate_limit_per_minute !== original.rate_limit_per_minute) {
    patch.rate_limit_per_minute = next.rate_limit_per_minute;
  }
  return patch;
}

/**
 * The address as the backend stores and compares it (RFC 5952 for IPv6), or null when it is not one. A
 * non-canonical IPv6 would be saved as typed and never match a request.
 */
export function canonicalIp(raw: string): string | null {
  const ip = raw.trim().toLowerCase().replace(MAPPED_IPV4, "");
  if (IPV4.test(ip)) return ip;
  if (!ip.includes(":") || !/^[0-9a-f:.]+$/.test(ip)) return null;
  try {
    return new URL(`http://[${ip}]`).hostname.slice(1, -1);
  } catch {
    return null;
  }
}

/** The waiver box only means something on a key that grants one of the IP-optional permissions. */
export function offersIpWaiver(
  values: Pick<InternalApiKeyFormValues, "all_permissions" | "permissions">,
  ipOptionalPermissions: readonly InternalApiKeyPermission[],
): boolean {
  if (ipOptionalPermissions.length === 0) return false;
  return (
    values.all_permissions ||
    values.permissions.some((permission) => ipOptionalPermissions.includes(permission))
  );
}

export type IpProblem = "invalid" | "duplicate";

/**
 * Format and duplicates only: whether the address is public is the backend's call, it alone knows the
 * environment.
 */
export function ipProblem(raw: string, existing: readonly InternalApiKeyAllowedIp[]): IpProblem | null {
  const ip = canonicalIp(raw);
  if (!ip) return "invalid";
  if (existing.some((entry) => entry.ip === ip)) return "duplicate";
  return null;
}

export type StatusTone = "success" | "neutral" | "warning" | "danger";

export const STATUS_TONE: Record<InternalApiKeyStatus, StatusTone> = {
  ACTIVE: "success",
  DISABLED: "neutral",
  NO_IP: "warning",
  EXPIRED: "warning",
  REVOKED: "danger",
};
